import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { TicketDetailSkeleton } from './LoadingSkeletons';
import { t } from '../i18n';
import { getConfig } from '../config';
import { getTickets, getTicket, createTicket, replyTicket, resolveTicket, getTicketAttachmentUrl, sendHelpdeskAuth } from '../api';
import { store } from '../store';
import { formatTicketDateTime } from '../utils/timezone';
import { ticketIdFromPath } from '../ticketPath';

const PER_PAGE = 10;

/** List chrome shares orders table tokens; keep attention edge via td border-left. */

function asArray<T = any>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object') return Object.values(value as Record<string, T>);
  return [];
}

function ticketListSortTs(row: { date_created?: string }): number {
  const s = row?.date_created;
  if (!s) return 0;
  const n = Date.parse(String(s).trim().replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/, '$1T$2'));
  return Number.isFinite(n) ? n : 0;
}

/** Newest first; also stabilizes order when API returns an object map (Object.values order is undefined). */
function sortTicketsNewestFirst(list: any[]): any[] {
  return [...list].sort((a, b) => ticketListSortTs(b) - ticketListSortTs(a));
}

function ticketStatusLabel(status: string): string {
  if (status === 'awaiting_user') return t('tickets.statusAwaitingUser');
  if (status === 'awaiting_agent') return t('tickets.statusAwaitingAgent');
  if (status === 'pending') return t('tickets.statusPending');
  if (status === 'resolved') return t('tickets.statusResolved');
  return status;
}

function ticketStatusClass(status: string): string {
  if (status === 'awaiting_user') return 'sw-ticket-status sw-ticket-status--awaiting-user';
  if (status === 'awaiting_agent') return 'sw-ticket-status sw-ticket-status--awaiting-agent';
  if (status === 'pending') return 'sw-ticket-status sw-ticket-status--pending';
  if (status === 'resolved') return 'sw-ticket-status sw-ticket-status--resolved';
  return 'sw-ticket-status';
}

function TicketDateCell({ value }: { value?: string }) {
  const { date, time, title } = formatTicketDateTime(value);
  return (
    <div class="sw-ticket-date" title={title}>
      <span class="sw-ticket-date__day">{date}</span>
      {time ? <span class="sw-ticket-date__time">{time}</span> : null}
    </div>
  );
}

function TicketRowChevron() {
  return (
    <svg class="sw-ticket-row-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

function decodeEscapedHtml(input: string): string {
  if (!input || typeof document === 'undefined') return input;
  let out = input.replace(/\\\//g, '/');
  // DeskPRO payload can be encoded more than once: &amp;lt;p&amp;gt;...
  for (let i = 0; i < 3; i++) {
    if (!/[&](lt|gt|amp|quot|apos|#\d+|#x[0-9a-f]+);/i.test(out)) break;
    const el = document.createElement('textarea');
    el.innerHTML = out;
    const decoded = el.value || out;
    if (decoded === out) break;
    out = decoded;
  }
  return out;
}

/** DeskPro may return either `message_raw` or escaped HTML in `message`. */
function TicketMessageBody({ msg }: { msg: { message?: string; message_raw?: string; person?: { is_agent?: boolean } } }) {
  const html = decodeEscapedHtml((msg.message_raw && msg.message_raw.trim() !== '' ? msg.message_raw : msg.message) ?? '');
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.innerHTML = html;
  }, [html]);
  return <div class="sw-ticket-msg-html" ref={bodyRef} />;
}

function isAgentMessage(msg: { person?: { is_agent?: boolean } }): boolean {
  return Boolean(msg?.person?.is_agent);
}

function messageRowKey(msg: { id?: string | number; date_created?: string; person?: { name?: string } }, idx: number): string {
  if (msg?.id != null) return String(msg.id);
  return `${msg?.date_created ?? ''}:${msg?.person?.name ?? ''}:${idx}`;
}

function TicketFilePicker({
  files,
  onChange,
  inputRef,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  inputRef?: { current: HTMLInputElement | null };
}) {
  const localRef = useRef<HTMLInputElement>(null);
  const ref = inputRef ?? localRef;
  const label = files.length
    ? t('tickets.filesCount', { count: files.length })
    : t('tickets.attachFiles');

  return (
    <div class="sw-ticket-files">
      <input
        ref={ref}
        type="file"
        class="d-none"
        multiple
        accept=".png, .jpg, .jpeg, .bmp, .pdf, .txt"
        onChange={(e) => {
          const target = e.currentTarget;
          const picked = target.files ? Array.from(target.files).slice(0, 3) : [];
          onChange(picked);
        }}
      />
      <button type="button" class="btn btn-sm btn-outline-secondary" onClick={() => ref.current?.click()}>
        {label}
      </button>
      <small class="text-muted d-block mt-1">{t('tickets.fileLimit')}</small>
      {files.length > 0 && (
        <ul class="sw-ticket-files__list mb-0 mt-2">
          {files.map((f) => (
            <li key={`${f.name}-${f.size}-${f.lastModified}`}>{f.name}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TicketsView({ config }: { config: ReturnType<typeof getConfig> }) {
  const initialTicketId = typeof window !== 'undefined' ? ticketIdFromPath(window.location.pathname) : null;
  const [view, setView] = useState<'list' | 'detail' | 'new' | 'inactive'>(initialTicketId ? 'detail' : 'list');
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(initialTicketId);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [_perPage, setPerPage] = useState(PER_PAGE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadInProgressRef = useRef(false);
  const hasMore = tickets.length < total;

  const loadPage = useCallback((p: number, append: boolean, background = false) => {
    if (loadInProgressRef.current) return;
    loadInProgressRef.current = true;
    const setBusy = append ? setLoadingMore : background ? () => {} : setLoading;
    if (!background) setBusy(true);
    getTickets(config, p).then((res) => {
      if (!background) setBusy(false);
      if (res.status === 200 && res.result) {
        const result = res.result as any;
        const list = sortTicketsNewestFirst(asArray(result.tickets));
        setTotal(result.total ?? 0);
        setPerPage(result.per_page ?? PER_PAGE);
        setPage(p);
        setTickets((prev) => (append ? [...prev, ...list] : list));
        const ids = list.map((t: any) => t.id).slice(0, 5);
        const prefetch = () => ids.forEach((id: string) => getTicket(config, id).catch(() => {}));
        if (typeof requestIdleCallback !== 'undefined') requestIdleCallback(prefetch, { timeout: 2000 });
        else setTimeout(prefetch, 100);
      } else if (res.status === 401) {
        setView('inactive');
      } else if (!append && !background) {
        setError(t(res.error || 'tickets.loadFailed'));
      }
    }).finally(() => {
      loadInProgressRef.current = false;
      if (!background) {
        setLoading(false);
        setLoadingMore(false);
      }
    });
  }, [config]);

  const refreshList = useCallback(() => {
    if (refreshing || loadInProgressRef.current) return;
    setRefreshing(true);
    getTickets(config, 1).then((res) => {
      if (res.status === 200 && res.result) {
        const result = res.result as any;
        const list = sortTicketsNewestFirst(asArray(result.tickets));
        setTotal(result.total ?? 0);
        setPerPage(result.per_page ?? PER_PAGE);
        setPage(1);
        setTickets(list);
        const ids = list.map((t: any) => t.id).slice(0, 5);
        const prefetch = () => ids.forEach((id: string) => getTicket(config, id).catch(() => {}));
        if (typeof requestIdleCallback !== 'undefined') requestIdleCallback(prefetch, { timeout: 2000 });
        else setTimeout(prefetch, 100);
      }
    }).finally(() => setRefreshing(false));
  }, [config, refreshing]);

  useEffect(() => {
    if (view === 'list') {
      loadPage(1, false);
    }
  }, [view]);

  useEffect(() => {
    if (view !== 'list' || !hasMore || loadingMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loadingMore && !loading) {
          loadPage(page + 1, true);
        }
      },
      { rootMargin: '100px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [view, hasMore, loadingMore, loading, page, loadPage]);

  if (view === 'inactive') {
    return <TicketsInactiveView config={config} />;
  }

  if (view === 'detail' && selectedTicketId) {
    return <TicketDetailView config={config} ticketId={selectedTicketId} onBack={() => { setSelectedTicketId(null); setView('list'); }} />;
  }

  if (view === 'new') {
    return <NewTicketView config={config} onBack={() => setView('list')} onSuccess={() => { setView('list'); loadPage(1, false, true); }} />;
  }

  if (loading) {
    return (
      <div class="sw-tickets-shell" data-sw-ui="tickets-v5">
        <div class="sw-page-chrome placeholder-glow" aria-hidden="true">
          <div class="sw-page-chrome__heading">
            <span class="placeholder col-3 d-block" style={{ height: '1.6rem', maxWidth: '12rem' }} />
            <span class="placeholder col-1 d-block" style={{ height: '1rem', maxWidth: '3rem' }} />
          </div>
          <div class="sw-page-chrome__actions">
            <span class="placeholder" style={{ height: '2rem', width: '8rem' }} />
            <span class="placeholder" style={{ height: '2rem', width: '5.5rem' }} />
          </div>
        </div>
        <div class="sw-tickets-list placeholder-glow" style="padding:0.75rem 0" aria-busy="true" aria-label={t('tickets.loading')}>
          <div class="table-responsive d-none d-md-block">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{t('tickets.subject')}</th>
                  <th>{t('tickets.status')}</th>
                  <th>{t('tickets.date')}</th>
                  <th>{t('tickets.lastReply')}</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td><span class="placeholder col-10 d-block" style={{ height: '1rem' }} /></td>
                    <td><span class="placeholder col-5 d-block" style={{ height: '1.25rem' }} /></td>
                    <td><span class="placeholder col-7 d-block" style={{ height: '1rem' }} /></td>
                    <td><span class="placeholder col-7 d-block" style={{ height: '1rem' }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div class="d-md-none" style={{ padding: '0.75rem' }}>
            {[1, 2, 3].map((i) => (
              <article key={i} class="sw-ticket-card mb-3">
                <span class="placeholder col-11 d-block mb-2" style={{ height: '1rem' }} />
                <span class="placeholder col-4 d-block mb-2" style={{ height: '1.25rem' }} />
                <span class="placeholder col-8 d-block" style={{ height: '0.875rem' }} />
              </article>
            ))}
          </div>
        </div>
      </div>
    );
  }
  if (error) return (
    <div class="alert alert-danger">
      <span style="display:inline-flex;align-items:center;gap:.4rem">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
          <path d="M12 7v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          <circle cx="12" cy="16.5" r="1" fill="currentColor" />
        </svg>
        {error}
      </span>
    </div>
  );

  const openTicket = (ticketId: string) => {
    setSelectedTicketId(ticketId);
    setView('detail');
  };

  return (
    <div class="sw-tickets-shell" data-sw-ui="tickets-v5">
      <div class="sw-page-chrome">
        <div class="sw-page-chrome__heading">
          <h1 class="sw-page-chrome__title">{t('tickets.title')}</h1>
          <span class="sw-page-chrome__meta">{tickets.length} / {total}</span>
        </div>
        <div class="sw-page-chrome__actions">
          <button class="btn btn-primary btn-sm" onClick={() => setView('new')}>{t('tickets.openNew')}</button>
          <button class="btn btn-outline-secondary btn-sm" onClick={refreshList} disabled={refreshing} title={t('tickets.refresh')}>
            {refreshing ? <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ marginRight: '.25rem' }}>
                <path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            )}
            {t('tickets.refresh')}
          </button>
        </div>
      </div>

      <div class="sw-tickets-list">
      {tickets.length === 0 ? (
        <div class="sw-tickets-list__empty alert alert-info text-center mb-0">{t('tickets.empty')}</div>
      ) : (
        <>
          <div class="sw-tickets-desktop">
            <div class="sw-tickets-table-wrap table-responsive">
              <table class="table table-sm align-middle sw-tickets-table">
                <thead>
                  <tr>
                    <th class="sw-tickets-table__subject">{t('tickets.subject')}</th>
                    <th class="sw-tickets-table__status">{t('tickets.status')}</th>
                    <th class="sw-tickets-table__date">{t('tickets.date')}</th>
                    <th class="sw-tickets-table__date">{t('tickets.lastReply')}</th>
                    <th class="sw-tickets-table__action" scope="col"><span class="sw-tickets-table__action-label" aria-hidden="true"></span></th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      class={`sw-tickets-table__row${ticket.status === 'awaiting_user' ? ' sw-tickets-table__row--attention' : ''}`}
                      tabIndex={0}
                      role="button"
                      onClick={() => openTicket(ticket.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openTicket(ticket.id);
                        }
                      }}
                    >
                      <td class="sw-tickets-table__subject">
                        <span class="sw-ticket-link">
                          {ticket.status === 'resolved' ? <del>{ticket.subject}</del> : ticket.subject}
                        </span>
                      </td>
                      <td class="sw-tickets-table__status">
                        <span class={ticketStatusClass(ticket.status)}>{ticketStatusLabel(ticket.status)}</span>
                      </td>
                      <td class="sw-tickets-table__date"><TicketDateCell value={ticket.date_created} /></td>
                      <td class="sw-tickets-table__date"><TicketDateCell value={ticket.date_status} /></td>
                      <td class="sw-tickets-table__action"><TicketRowChevron /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div class="sw-tickets-mobile">
            {tickets.map((ticket) => (
              <article
                key={`m-${ticket.id}`}
                class={`sw-ticket-card${ticket.status === 'awaiting_user' ? ' sw-ticket-card--attention' : ''}`}
                tabIndex={0}
                role="button"
                onClick={() => openTicket(ticket.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openTicket(ticket.id);
                  }
                }}
              >
                <div class="sw-ticket-card__head">
                  <span class="sw-ticket-link">
                    {ticket.status === 'resolved' ? <del>{ticket.subject}</del> : ticket.subject}
                  </span>
                  <TicketRowChevron />
                </div>
                <div class="sw-ticket-card__row">
                  <span class={ticketStatusClass(ticket.status)}>{ticketStatusLabel(ticket.status)}</span>
                </div>
                <div class="sw-ticket-card__meta">
                  <span><strong>{t('tickets.date')}:</strong> {formatTicketDateTime(ticket.date_created).compact}</span>
                  <span><strong>{t('tickets.lastReply')}:</strong> {formatTicketDateTime(ticket.date_status).compact}</span>
                </div>
              </article>
            ))}
          </div>
          <div ref={sentinelRef} class="d-flex justify-content-center py-2" style={{ minHeight: '24px' }}>
            {loadingMore && <span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>}
            {loadingMore && <span class="ms-2 text-muted small">{t('tickets.loadingMore')}</span>}
          </div>
        </>
      )}
    </div>
    </div>
  );
}

function TicketDetailView({ config, ticketId, onBack }: { config: ReturnType<typeof getConfig>, ticketId: string, onBack: () => void }) {
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadTicket = (silent = false) => {
    if (silent) {
      getTicket(config, ticketId).then((res) => {
        if (res.status === 200 && res.result) setTicket(res.result);
      });
      return;
    }
    setLoading(true);
    getTicket(config, ticketId).then((res) => {
      setLoading(false);
      if (res.status === 200 && res.result) {
        setTicket(res.result);
      } else if (res.status === 401) {
        setError(t('tickets.notActivated'));
      } else {
        setError(t(res.error || 'tickets.loadFailed'));
      }
    });
  };

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    getTicket(config, ticketId).then((res) => {
      if (res.status === 200 && res.result) setTicket(res.result);
    }).finally(() => setRefreshing(false));
  };

  useEffect(() => {
    loadTicket();
  }, [ticketId]);

  const handleResolve = async () => {
    if (!confirm(t('tickets.resolveConfirm'))) return;
    setLoading(true);
    const res = await resolveTicket(config, ticketId);
    setLoading(false);
    if (res.status === 200) {
      loadTicket(true);
    } else {
      alert(t(res.error || 'tickets.resolveFailed'));
    }
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    const res = await replyTicket(config, ticketId, message, files);
    setSending(false);

    if (res.status === 200) {
      setMessage('');
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadTicket(true);
    } else {
      alert(t(res.error || 'tickets.replyFailed'));
    }
  };

  if (loading) return <TicketDetailSkeleton />;
  if (error) return <div class="alert alert-danger">{error} <button class="btn btn-link" onClick={onBack}>{t('orders.back')}</button></div>;
  if (!ticket) return null;

  const messages = asArray(ticket.messages);

  return (
    <div class="sw-tickets-shell">
      <div class="sw-ticket-detail">
        <div class="sw-ticket-detail__nav">
          <button class="btn btn-sm btn-outline-secondary" onClick={onBack}>
            <span class="sw-ticket-detail__nav-label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {t('tickets.backToTickets')}
            </span>
          </button>
          <button class="btn btn-sm btn-outline-secondary" onClick={handleRefresh} disabled={refreshing} title={t('tickets.refresh')}>
            {refreshing ? <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ marginRight: '.25rem' }}>
                <path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            )}
            {t('tickets.refresh')}
          </button>
        </div>

        <header class="sw-ticket-detail__header">
          <div class="sw-ticket-detail__heading">
            <h1 class="sw-ticket-detail__subject">{ticket.subject}</h1>
            <div class="sw-ticket-detail__meta">
              <span class={ticketStatusClass(ticket.status)}>{ticketStatusLabel(ticket.status)}</span>
              <time class="sw-ticket-detail__date" title={formatTicketDateTime(ticket.date_created).title}>
                {formatTicketDateTime(ticket.date_created).compact}
              </time>
            </div>
          </div>
          {ticket.status !== 'resolved' && (
            <button class="btn btn-outline-secondary btn-sm sw-ticket-detail__resolve" onClick={handleResolve}>
              {t('tickets.resolve')}
            </button>
          )}
        </header>

        <section class="sw-ticket-thread" aria-label={t('tickets.messagesHeading')}>
          <h2 class="sw-ticket-thread__title">{t('tickets.messagesHeading')}</h2>
          {messages.length > 0 ? (
            <div class="sw-ticket-thread__list">
              {messages.map((msg: any, idx: number) => {
                const agent = isAgentMessage(msg);
                return (
                  <article
                    key={messageRowKey(msg, idx)}
                    class={`sw-ticket-msg${agent ? ' sw-ticket-msg--agent' : ' sw-ticket-msg--user'}`}
                  >
                    <div class="sw-ticket-msg__rail" aria-hidden="true" />
                    <div class="sw-ticket-msg__body">
                      <div class="sw-ticket-msg__head">
                        <div class="sw-ticket-msg__who">
                          <span class={`sw-ticket-msg__role${agent ? ' sw-ticket-msg__role--agent' : ''}`}>
                            {agent ? t('tickets.messageFromSupport') : t('tickets.messageFromYou')}
                          </span>
                          <span class="sw-ticket-msg__name">
                            {msg.person?.name || (agent ? t('tickets.messageFromSupport') : t('tickets.messageFromYou'))}
                          </span>
                        </div>
                        <time class="sw-ticket-msg__time" title={formatTicketDateTime(msg.date_created).title}>
                          {formatTicketDateTime(msg.date_created).compact}
                        </time>
                      </div>
                      <TicketMessageBody msg={msg} />
                      {asArray(msg.attachments).length > 0 && (
                        <div class="sw-ticket-msg__attachments">
                          {asArray(msg.attachments).map((att: any, attIdx: number) => (
                            <a
                              key={att?.blob?.id ?? attIdx}
                              href={getTicketAttachmentUrl(config, ticketId, att.blob.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              class="sw-ticket-msg__attachment"
                            >
                              {att.blob.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div class="alert alert-info text-center mb-0">{t('tickets.noMessages')}</div>
          )}
        </section>

        {ticket.status !== 'resolved' && (
          <div class="sw-ticket-reply">
            <h2 class="sw-ticket-reply__title">{t('tickets.replyHeading')}</h2>
            <form onSubmit={handleSubmit}>
              <div class="mb-3">
                <label class="form-label" for="sw-ticket-reply-message">{t('tickets.message')}</label>
                <textarea
                  id="sw-ticket-reply-message"
                  class="form-control"
                  rows={5}
                  value={message}
                  onInput={(e) => setMessage(e.currentTarget.value)}
                  required
                />
              </div>
              <div class="mb-3">
                <TicketFilePicker files={files} onChange={setFiles} inputRef={fileInputRef} />
              </div>
              <div class="sw-ticket-reply__actions">
                <button type="submit" class="btn btn-primary" disabled={sending}>
                  {sending ? <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : null}
                  {t('tickets.sendReply')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function NewTicketView({ config, onBack, onSuccess }: { config: ReturnType<typeof getConfig>, onBack: () => void, onSuccess: () => void }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;

    setSending(true);
    const res = await createTicket(config, subject, message, files, config.siteKey);
    setSending(false);

    if (res.status === 200) {
      store.addToast(t('tickets.createSuccess'), 'success');
      onSuccess();
    } else {
      alert(t(res.error || 'tickets.createFailed'));
    }
  };

  return (
    <div class="sw-tickets-shell">
      <div class="sw-ticket-form-card">
      <div class="sw-ticket-form-card__header">
        <button class="btn btn-sm btn-outline-secondary" onClick={onBack}>
          <span class="sw-ticket-detail__nav-label">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {t('tickets.backToTickets')}
          </span>
        </button>
        <h4 class="sw-ticket-form-card__title">{t('tickets.newTicketTitle')}</h4>
      </div>

      <form onSubmit={handleSubmit} class="sw-ticket-form-card__body">
        <div class="mb-3">
          <label class="form-label">{t('tickets.newSubject')}</label>
          <input type="text" class="form-control" value={subject} onInput={(e) => setSubject(e.currentTarget.value)} required />
        </div>

        <div class="mb-3">
          <label class="form-label">{t('tickets.newMessage')}</label>
          <textarea class="form-control" rows={5} value={message} onInput={(e) => setMessage(e.currentTarget.value)} required></textarea>
        </div>

        <div class="mb-3">
          <TicketFilePicker files={files} onChange={setFiles} />
        </div>

        <div class="sw-ticket-reply__actions">
          <button type="submit" class="btn btn-primary" disabled={sending}>
            {sending ? <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : null}
            {t('tickets.submitTicket')}
          </button>
        </div>
      </form>
    </div>
    </div>
  );
}

function TicketsInactiveView({ config }: { config: ReturnType<typeof getConfig> }) {
  const [sending, setSending] = useState(false);

  const handleActivate = async () => {
    setSending(true);
    const res = await sendHelpdeskAuth(config);
    setSending(false);

    if (res.status === 200 && (res as { result?: boolean }).result !== false) {
      store.addToast(t('tickets.activationSuccess'), 'success');
    } else {
      alert((res as { error?: string }).error || t('tickets.activationFailed'));
    }
  };

  return (
    <div class="sw-tickets-shell">
      <div class="sw-ticket-form-card text-center py-5">
      <h4 class="mb-3">{t('tickets.authNotActivated')}</h4>
      <h5 class="mb-4 text-muted">{t('tickets.authInstructions')}</h5>
      <button class="btn btn-primary" onClick={handleActivate} disabled={sending}>
        {sending ? <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : null}
        {t('tickets.sendActivation')}
      </button>
      </div>
    </div>
  );
}
